/**
 * Default style and content values for new layers.
 *
 * Source of truth for layer creation defaults — used by:
 *   - API MCP write tools (addLayer operation)
 *   - apps/web (AdUtils.createCreativeUnitLayer — migrate to import from here)
 *
 * These defaults ensure layers are immediately visible in the renderer
 * with proper positioning, dimensions, and type-specific visual styles.
 */

// ---------------------------------------------------------------------------
// Layout defaults (position, dimensions, display)
// ---------------------------------------------------------------------------

export const DEFAULT_LAYER_Z_INDEX = 50;

const BASE_LAYOUT_STYLE: Record<string, unknown> = {
  display: 'block',
  position: 'absolute',
  overflow: 'hidden',
  width: '20cqw',
  height: '20cqh',
  left: '0',
  top: '0',
  bottom: 'auto',
  right: 'auto',
  zIndex: DEFAULT_LAYER_Z_INDEX,
};

const LAYOUT_OVERRIDES: Record<string, Record<string, unknown>> = {
  text: { overflow: 'visible', overflowWrap: 'break-word' },
  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '15cqmax',
    height: '7cqmax',
    containerType: 'size',
    boxSizing: 'border-box',
    fontSize: '10cqh',
    paddingTop: '10cqh',
    paddingBottom: '10cqh',
    paddingLeft: '15cqw',
    paddingRight: '15cqw',
    overflow: 'hidden',
  },
  shape: { boxSizing: 'border-box' },
  group: { containerType: 'size' },
};

// ---------------------------------------------------------------------------
// Visual defaults (colors, fonts)
// ---------------------------------------------------------------------------

const VISUAL_DEFAULTS: Record<string, Record<string, unknown>> = {
  text: { color: '#FFFFFF', whiteSpace: 'pre-wrap' },
  button: {
    backgroundColor: '#000000',
    color: '#ffffff',
    borderRadius: '2cqw',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre',
  },
  image: { objectFit: 'cover' },
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the default layout styles for a given layer type.
 * Base styles (position, dimensions) + type-specific overrides.
 */
export function getDefaultLayoutStyles(layerType: string): Record<string, unknown> {
  return {
    ...BASE_LAYOUT_STYLE,
    ...(LAYOUT_OVERRIDES[layerType] || {}),
  };
}

/**
 * Returns the default visual styles for a given layer type.
 * Colors, fonts, and type-specific visual properties.
 */
export function getDefaultVisualStyles(layerType: string): Record<string, unknown> {
  return VISUAL_DEFAULTS[layerType] || {};
}

/**
 * Returns the combined layout + visual default styles for a given layer type.
 * If `overrides` are provided, they are merged on top (overrides win).
 */
export function getDefaultLayerStyle(
  layerType: string,
  overrides?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...getDefaultLayoutStyles(layerType),
    ...getDefaultVisualStyles(layerType),
    ...(overrides || {}),
  };
}

// ---------------------------------------------------------------------------
// Agent-flow layer factory
// ---------------------------------------------------------------------------

/**
 * Params accepted by `buildAgentCreatedLayer`. Mirrors the addLayer op
 * params accepted by both `edit_master_template` and `edit_cart_item` so
 * both handlers can pass through their parsed params unchanged.
 */
export interface AgentCreatedLayerParams {
  id: string;
  type: string;
  label?: string;
  content?: Record<string, unknown>;
  style?: Record<string, unknown>;
  interactivity?: Record<string, unknown>;
}

/**
 * Build a fully-defaulted Layer object for AGENT-driven `addLayer` ops.
 *
 * Single source of truth for the defaults applied when an agent (master
 * template or cart-item scope) creates a new layer. Both
 * `apps/api/src/mcp/runtime/tools/edit-master-template.ts` and
 * `apps/api/src/mcp/runtime/tools/edit-cart-item.ts` use these defaults so
 * their addLayer outputs stay in lockstep — when a default is tightened
 * (or relaxed) here, both surfaces inherit it.
 *
 * Web UI flows (`AdUtils.createCreativeUnitLayer` /
 * `LayerCreationUtils.createCreativeUnitLayer`) do NOT go through this
 * helper directly; they have richer per-type placeholder-content logic
 * suited to UI insertion and call `getDefaultLayerStyle` themselves.
 * Both code paths share the same underlying style defaults via
 * `getDefaultLayerStyle`.
 *
 * Defaults applied:
 *   - `style`: combined layout + visual defaults for the layer type, with
 *     any agent-provided `style` merged on top (agent wins).
 *   - `interactivity`: defaults to `{enabled:true, styleInteractivity:true}`
 *     so agent-created layers are immediately selectable and editable in the
 *     canvas. Agent-provided `interactivity` overrides the default.
 *   - **Per-type renderable content** (so agents can't accidentally produce
 *     blank/un-renderable layers — see `applyContentDefaults` below):
 *     - `text`: ensures `text.content` is non-empty (default: "Text").
 *     - `button`: ensures `text.content` is non-empty AND `smart: true`
 *       (default: "Button"). Buttons render their label from
 *       `text.content`, not from `button.label`. If an agent passes
 *       `content.button.label` (legacy schema), it's lifted into
 *       `text.content` automatically.
 *     - `html`: lifts legacy `html.code` (the pre-U1 MCP shape) into
 *       `html.content` so legacy clients / cached agent contexts that
 *       still emit `html.code` keep rendering instead of failing
 *       validation. The renderer reads `html.content` exclusively
 *       (apps/web/.../ad-renderer.class.ts:619-635).
 *     - Other types (image/video/shape/audio/group) pass through
 *       agent-provided content unchanged. If those types come up missing
 *       a required content field, agents are expected to provide it
 *       explicitly per the schema description.
 *
 * Spread shape: { id, type, [label], ...content (with per-type defaults
 * merged in), style, interactivity }. `label` is omitted when not
 * provided so callers don't accidentally label a layer with its type-name.
 */
export function buildAgentCreatedLayer(
  params: AgentCreatedLayerParams,
): Record<string, unknown> {
  const content = applyContentDefaults(
    params.type,
    (params.content || {}) as Record<string, unknown>,
  );
  return {
    id: params.id,
    type: params.type,
    ...(params.label ? { label: params.label } : {}),
    ...content,
    style: getDefaultLayerStyle(params.type, params.style),
    interactivity: params.interactivity ?? { enabled: true, styleInteractivity: true },
  };
}

/**
 * Inject per-type renderable-content defaults so agents can't produce
 * blank layers. See the JSDoc on `buildAgentCreatedLayer` for the rules
 * applied per type. Pure function — does not mutate `content`.
 */
function applyContentDefaults(
  type: string,
  content: Record<string, unknown>,
): Record<string, unknown> {
  if (type === 'text') {
    const existingText = content.text as Record<string, unknown> | undefined;
    const textContent = (existingText?.content as string) ?? 'Text';
    return {
      ...content,
      text: {
        ...(existingText || {}),
        content: textContent,
      },
    };
  }
  if (type === 'button') {
    const existingText = content.text as Record<string, unknown> | undefined;
    const existingButton = content.button as Record<string, unknown> | undefined;
    // Renderer reads button-displayed text from `text.content`, NOT from
    // `button.label`. Lift `button.label` (legacy/agent fallback) into
    // `text.content` when text.content is missing.
    const textContent =
      (existingText?.content as string) ??
      (existingButton?.label as string) ??
      'Button';
    return {
      ...content,
      text: {
        ...(existingText || {}),
        content: textContent,
        smart: existingText?.smart ?? true,
      },
    };
  }
  if (type === 'html') {
    const existingHtml = content.html as Record<string, unknown> | undefined;
    // Renderer reads HTML markup from `html.content`, NOT from
    // `html.code`. Pre-U1 MCP DTOs declared `html.code`, so lift that
    // legacy field into `html.content` when content is missing. This
    // keeps cached agent contexts and any in-flight legacy callers from
    // hitting a hard validation error after the U1 rename.
    if (existingHtml && typeof existingHtml === 'object') {
      const hasContent = 'content' in existingHtml;
      const legacyCode = existingHtml.code as string | undefined;
      if (!hasContent && typeof legacyCode === 'string') {
        const { code: _legacyCode, ...rest } = existingHtml as {
          code?: unknown;
          [key: string]: unknown;
        };
        return {
          ...content,
          html: {
            ...rest,
            content: legacyCode,
          },
        };
      }
    }
    return content;
  }
  return content;
}
